/**
 * Members, stewards and self-serve households (docs/members.md): the pure parts the server and
 * both clients share, so they agree on what an invite code looks like, what a household's id
 * becomes, who outranks whom, and say the same words when something is refused. No imports: this
 * file runs in a Pages Function, the Preact app, the native app and Node's test runner.
 */

/** A device's role in one household. The role belongs to the device session, not to a peon name. */
export type Role = "steward" | "peon";

/** Every role, lowest first. */
export const ROLES: readonly Role[] = ["peon", "steward"];

export function isRole(v: unknown): v is Role {
  return v === "steward" || v === "peon";
}

/** Order of the roles: a steward outranks a peon. */
export function roleRank(role: Role): number {
  return role === "steward" ? 1 : 0;
}

/** The higher of two roles (an invite never lowers a device already in the household). */
export function higherRole(a: Role, b: Role): Role {
  return roleRank(a) >= roleRank(b) ? a : b;
}

// ---------- Invite codes ----------

/** The letters and digits an invite code is made of: no 0, O, 1 or I, so it reads back the same. */
export const INVITE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
export const INVITE_CODE_LENGTH = 8;
/** How long an invite stays good. */
export const INVITE_TTL_DAYS = 7;
/** The most devices one invite can bring in. */
export const INVITE_MAX_USES = 20;

/** What a person typed, as a code: uppercase, spaces and dashes gone. */
export function normalizeInviteCode(input: string): string {
  return input.toUpperCase().replace(/[\s-]+/g, "");
}

/** True for a code in its normalized form: exactly eight characters of INVITE_ALPHABET. */
export function isInviteCode(code: unknown): code is string {
  if (typeof code !== "string" || code.length !== INVITE_CODE_LENGTH) return false;
  for (const ch of code) if (!INVITE_ALPHABET.includes(ch)) return false;
  return true;
}

/**
 * A fresh code from `bytes` (at least INVITE_CODE_LENGTH random bytes; the server passes
 * crypto.getRandomValues). The alphabet has 32 characters, so the low five bits of a byte pick
 * one without bias.
 */
export function inviteCodeFrom(bytes: ArrayLike<number>): string {
  let out = "";
  for (let i = 0; i < INVITE_CODE_LENGTH; i++) out += INVITE_ALPHABET[bytes[i] & 31];
  return out;
}

/** The link that opens the app at the invite: `<origin>/join/<code>`. */
export function joinLink(origin: string, code: string): string {
  return `${origin.replace(/\/+$/, "")}/join/${code}`;
}

// ---------- Household names, ids and passwords ----------

export const HOUSEHOLD_NAME_MAX = 40;
export const HOUSEHOLD_PASSWORD_MIN = 8;
export const HOUSEHOLD_PASSWORD_MAX = 200;
const SLUG_MAX = 32;
/** The id a name with no usable letters or digits gets. */
const SLUG_FALLBACK = "house";

/** A household's name as stored: trimmed, inner whitespace collapsed. */
export function normalizeHouseholdName(name: string): string {
  return name.trim().replace(/\s+/g, " ");
}

/** Why `name` cannot name a household, or null. Checks the normalized form. */
export function householdNameError(name: unknown): string | null {
  if (typeof name !== "string" || normalizeHouseholdName(name).length === 0) return MEMBER_MESSAGES.nameRequired;
  if (normalizeHouseholdName(name).length > HOUSEHOLD_NAME_MAX) return MEMBER_MESSAGES.nameTooLong;
  return null;
}

/** Why `password` cannot be a household's password, or null. Whether another household uses it is the server's to say. */
export function householdPasswordError(password: unknown): string | null {
  if (typeof password !== "string" || password.length < HOUSEHOLD_PASSWORD_MIN) return MEMBER_MESSAGES.passwordTooShort;
  if (password.length > HOUSEHOLD_PASSWORD_MAX) return MEMBER_MESSAGES.passwordTooLong;
  return null;
}

/** The clients' check of the "password again" field. */
export function passwordRepeatError(password: string, again: string): string | null {
  return password === again ? null : MEMBER_MESSAGES.passwordsDiffer;
}

/**
 * A household's id from its name: lowercase letters, digits and dashes, 2 to 32 characters,
 * starting with a letter or digit (server/households.ts isValidHouseholdId). Accents are dropped
 * ("Émile" becomes "emile"), apostrophes vanish ("Omie's" becomes "omies"), anything else becomes
 * a dash. People never see it.
 */
export function slugFromName(name: string): string {
  const slug = name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/['‘’`]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, SLUG_MAX)
    .replace(/-+$/g, "");
  return slug.length >= 2 ? slug : SLUG_FALLBACK;
}

/** The n-th id to try for `base` (n from 1): the base itself, then `base-2`, `base-3`, kept within 32 characters. */
export function slugCandidate(base: string, n: number): string {
  if (n <= 1) return base;
  const suffix = `-${n}`;
  return `${base.slice(0, SLUG_MAX - suffix.length).replace(/-+$/g, "")}${suffix}`;
}

/** The first candidate for `base` that is not in `taken`. */
export function freeSlug(base: string, taken: ReadonlySet<string>): string {
  for (let n = 1; ; n++) {
    const id = slugCandidate(base, n);
    if (!taken.has(id)) return id;
  }
}

// ---------- The last steward ----------

/**
 * Whether changing one device leaves the household with no steward: `members` are the live
 * sessions, `target` the one changing, `next` its new role or "removed". A house with no steward
 * at all already (before its first steward invite) is not made worse by anything, so nothing is
 * refused there.
 */
export function leavesNoSteward(members: ReadonlyArray<{ session_id: string; role: Role }>, target: string, next: Role | "removed"): boolean {
  const before = members.filter((m) => m.role === "steward").length;
  if (before === 0) return false;
  const after = members.filter((m) => (m.session_id === target ? next === "steward" : m.role === "steward")).length;
  return after === 0;
}

// ---------- Creation limits ----------

/** Households one address may create in an hour, and all addresses together in a day. */
export const CREATE_LIMIT_PER_IP_HOUR = 5;
export const CREATE_LIMIT_PER_DAY = 50;

/**
 * The `meta` rows that count creations: `create:<ipHash>:<hour>` and `create:all:<day>`, where
 * `ipHash` is the first 16 hex characters of the SHA-256 of the client's address, the hour is
 * `YYYY-MM-DDTHH` and the day `YYYY-MM-DD`, both UTC.
 */
export function createLimitKeys(ipHash: string, now: Date): { ip: string; all: string } {
  const iso = now.toISOString();
  return { ip: `create:${ipHash}:${iso.slice(0, 13)}`, all: `create:all:${iso.slice(0, 10)}` };
}

// ---------- Words ----------

/** What the server says when it refuses, and what the clients show before asking it. */
export const MEMBER_MESSAGES = {
  stewardsOnly: "Stewards only",
  inviteOnly: "This household is invite only",
  inviteNotValid: "That code doesn't match an invite. Check it, or ask for a new one.",
  needSteward: "The house needs a steward",
  pickDifferentPassword: "Pick a different password",
  tryLater: "Try again later",
  wrongCreationCode: "Wrong creation code",
  nameRequired: "Name the household",
  nameTooLong: `Keep the name to ${HOUSEHOLD_NAME_MAX} characters`,
  passwordTooShort: `Use at least ${HOUSEHOLD_PASSWORD_MIN} characters`,
  passwordTooLong: `Keep the password to ${HOUSEHOLD_PASSWORD_MAX} characters`,
  passwordsDiffer: "The passwords do not match",
  nameMismatch: "Type the household's name exactly",
  noSuchDevice: "That device is not in this household",
  noSuchInvite: "That invite is not in this household",
  signOutHere: "Sign this device out from your account",
  /** Logout refused (409): this device is the last steward and its person has no way back in. */
  lastStewardSignOut: "The house needs a steward. Set a password or add a passkey first.",
} as const;

/** "Steward of Omie's" or "Peon of Omie's": the account sheet's role line. */
export function roleLine(role: Role, householdName: string): string {
  return `${role === "steward" ? "Steward" : "Peon"} of ${householdName}`;
}
